/**
 * Step 5 -- Exchange rates (f10.8), rendered inside f10.3's step frame through
 * `STEP_BODIES`. Follows `cycle-step.html`:
 *
 *   upload -> process -> what the system did -> resulting rates -> accept
 *
 * One feed and nothing to decide: a bad row is fixed by discarding and
 * uploading a corrected file. Shows the newest batch that has not been
 * discarded. Upload and commit each answer `202` with a job, watched through
 * the step's own job route; on a terminal state the batch (and, after a
 * commit, the rail) is refetched.
 */

import { useEffect, useRef, useState } from 'react';
import { Check, X } from 'lucide-react';

import type { CycleWorkflowStep, ExchangeRatesReview } from '@/api/client';
import {
  isJobTerminal,
  useCommitExchangeRateBatch,
  useCycle,
  useDiscardExchangeRateBatch,
  useExchangeRateBatches,
  useExchangeRateJob,
  useExchangeRateReview,
  useRefreshExchangeRates,
  useUploadExchangeRateBatch,
} from '@/api/queries';
import { JobProgress } from '@/jobs/JobProgress';

import {
  ExchangeRatesDropzone,
  ExchangeRatesFileCard,
  ExchangeRatesProcessingCard,
  ExchangeRatesTableCard,
} from './ExchangeRatesCards';
import { ExchangeRatesCommitDialog } from './ExchangeRatesCommitDialog';
import { isParsing, refusalMessage } from './exchangeRatesView';

interface WatchedJob {
  id: string;
  kind: 'parse' | 'commit';
}

export function ExchangeRatesStep({ cycleId }: { cycleId: string; step: CycleWorkflowStep }) {
  const batches = useExchangeRateBatches(cycleId);
  const upload = useUploadExchangeRateBatch(cycleId);
  const [watched, setWatched] = useState<WatchedJob | null>(null);

  const shown = batches.data?.batches.find((batch) => batch.state.code !== 'DISCARDED');
  const parsing = shown ? isParsing(shown.state.code) : false;
  const review = useExchangeRateReview(cycleId, shown && !parsing ? shown.id : undefined);
  const job = useExchangeRateJob(cycleId, watched?.id);

  useJobCompletion(cycleId, watched, job.data?.state);

  const startUpload = (file: File) =>
    upload.mutate(file, { onSuccess: (accepted) => setWatched({ id: accepted.job_id, kind: 'parse' }) });

  if (batches.isPending) return <p className="hint">Loading step 5…</p>;
  if (batches.isError) {
    return <ErrorAlert message={refusalMessage(batches.error) ?? 'Step 5 could not be loaded.'} />;
  }

  const jobCard = job.data && (!isJobTerminal(job.data.state) || job.data.state === 'FAILED') && (
    <div className="card">
      <div className="card-body">
        <JobProgress
          job={job.data}
          title={watched?.kind === 'commit' ? 'Storing the exchange rates' : 'Reading the exchange-rate list'}
        />
      </div>
    </div>
  );

  return (
    <div className="stack">
      {upload.error ? <ErrorAlert message={refusalMessage(upload.error)} /> : null}
      {jobCard}

      {!shown && <ExchangeRatesDropzone onFile={startUpload} busy={upload.isPending} />}

      {shown && parsing && !jobCard && (
        <p className="hint" role="status">
          Reading {shown.original_filename ?? 'the uploaded file'}…
        </p>
      )}

      {review.isError && <ErrorAlert message={refusalMessage(review.error)} />}

      {review.data && (
        <>
          <ExchangeRatesFileCard review={review.data} onReplace={startUpload} busy={upload.isPending} />
          <ExchangeRatesProcessingCard review={review.data} />
          <RatesAndAccept
            cycleId={cycleId}
            review={review.data}
            onCommitStarted={(jobId) => setWatched({ id: jobId, kind: 'commit' })}
          />
        </>
      )}
    </div>
  );
}

/** Refetch once a watched job reaches a terminal state -- once per job. */
function useJobCompletion(cycleId: string, watched: WatchedJob | null, state: string | undefined) {
  const refresh = useRefreshExchangeRates(cycleId);
  const handled = useRef<string | null>(null);

  useEffect(() => {
    if (!watched || !isJobTerminal(state) || handled.current === watched.id) return;
    handled.current = watched.id;
    refresh(watched.kind === 'commit' && state === 'SUCCEEDED');
  }, [watched, state, refresh]);
}

function RatesAndAccept({
  cycleId,
  review,
  onCommitStarted,
}: {
  cycleId: string;
  review: ExchangeRatesReview;
  onCommitStarted: (jobId: string) => void;
}) {
  const commit = useCommitExchangeRateBatch(cycleId, review.batch.id);
  const discard = useDiscardExchangeRateBatch(cycleId, review.batch.id);
  const cycle = useCycle(cycleId);
  const [confirming, setConfirming] = useState(false);
  const { capabilities } = review;

  const footer = (capabilities.can_commit || capabilities.can_discard) && (
    <div className="card-foot">
      <span className="hint">
        {capabilities.can_commit
          ? 'Accepting stores these rates for this cycle and completes step 5.'
          : 'An invalid row blocks the commit — discard this batch and upload a corrected file.'}
      </span>
      <div className="btn-row">
        {capabilities.can_discard && (
          <button type="button" className="btn btn-secondary" disabled={discard.isPending}
            onClick={() => discard.mutate()}>
            <X aria-hidden="true" /> Discard batch
          </button>
        )}
        <button type="button" className="btn btn-primary" disabled={!capabilities.can_commit}
          onClick={() => setConfirming(true)}>
          <Check aria-hidden="true" /> Accept and commit
        </button>
      </div>
    </div>
  );

  return (
    <>
      {discard.error ? <ErrorAlert message={refusalMessage(discard.error)} /> : null}
      <ExchangeRatesTableCard review={review} footer={footer || undefined} />
      {confirming && (
        <ExchangeRatesCommitDialog
          review={review}
          cycleCode={cycle.data?.code ?? '—'}
          busy={commit.isPending}
          error={commit.error}
          onClose={() => {
            setConfirming(false);
            commit.reset();
          }}
          onConfirm={() =>
            commit.mutate(undefined, {
              onSuccess: (accepted) => {
                setConfirming(false);
                onCommitStarted(accepted.job_id);
              },
            })
          }
        />
      )}
    </>
  );
}

function ErrorAlert({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="alert alert-danger" role="alert">
      <div>
        <div className="a-body">{message}</div>
      </div>
    </div>
  );
}
